import { useEffect, useState } from "react";
import { Download, Smartphone, ArrowRight } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    useInstallState,
    InstallActions,
    InstallLead,
    isIOS,
    isStandalone,
    isCompleted,
    isPopupShown,
} from "./installShared";

// Inline home banner — shown from the 2nd visit onwards (per device) when
// the app is not installed. Clicking it opens a modal that reuses the SAME
// content and instructions as the one-shot popup (single source of truth).
export default function InstallHomeBanner() {
    // The banner ignores per-browser dismiss TTLs (the popup handles those).
    // We only care about: NOT installed, popup already shown once.
    const install = useInstallState({ respectDismissTTL: false });
    const [open, setOpen] = useState(false);
    const [popupShown, setPopupShown] = useState(() => isPopupShown());

    // Listen for the popup-close event so the banner appears immediately
    // in the same session when the user dismisses the first-visit popup.
    useEffect(() => {
        const onClosed = () => setPopupShown(true);
        window.addEventListener("cn:install-popup-closed", onClosed);
        return () => window.removeEventListener("cn:install-popup-closed", onClosed);
    }, []);

    // iOS uses the dedicated permanent card (InstallHomeCardIOS) — this
    // banner is hidden on iPhone/iPad regardless of storage flags so users
    // never see two install cards stacked.
    const shouldSkip = isIOS() || install.installed || isStandalone() || isCompleted() || !popupShown;
    if (shouldSkip || !install.mode) return null;

    const handleNative = async () => {
        const choice = await install.triggerNativeInstall();
        if (choice && choice.outcome === "accepted") {
            setOpen(false);
        }
    };

    return (
        <section className="bg-white" data-testid="home-install-banner-section">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="home-install-banner"
                    className="group relative w-full text-left overflow-hidden rounded-3xl bg-gradient-to-r from-primary via-primary to-accent px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10 shadow-[0_10px_30px_-12px_rgba(48,131,201,0.55)] hover:shadow-[0_20px_45px_-15px_rgba(245,149,71,0.55)] hover:-translate-y-0.5 transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/40"
                >
                    <div className="pointer-events-none absolute -top-24 -right-16 size-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
                    <div className="pointer-events-none absolute -bottom-20 -left-10 size-56 rounded-full bg-accent/30 blur-3xl" aria-hidden="true" />

                    <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
                        <img
                            src="/apple-touch-icon.png"
                            alt="CondominioNow"
                            width={72}
                            height={72}
                            className="size-14 sm:size-16 rounded-2xl shrink-0 ring-2 ring-white/40 shadow-[0_8px_24px_-6px_rgba(0,0,0,0.35)]"
                        />

                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-white/80 font-semibold">
                                Installa l'app
                            </div>
                            <div className="mt-1 font-serif text-2xl sm:text-3xl font-semibold text-white leading-tight">
                                <span aria-hidden="true">📱</span> Porta CondominioNow sempre con te
                            </div>
                            <p className="mt-2 text-sm sm:text-base text-white/90 leading-relaxed max-w-2xl">
                                Le risposte ai tuoi dubbi condominiali sempre a portata di mano.
                            </p>
                        </div>

                        <span className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary font-semibold px-5 py-3 sm:px-6 sm:py-3.5 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.35)] group-hover:gap-3 group-hover:bg-accent group-hover:text-white transition-all duration-300 shrink-0 self-stretch sm:self-auto justify-self-stretch text-center">
                            <Download className="size-4" strokeWidth={2.5} />
                            <span>Installa</span>
                            <ArrowRight className="size-4" strokeWidth={2.5} />
                        </span>
                    </div>
                </button>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="sm:max-w-md"
                    data-testid="home-install-modal"
                    aria-describedby="install-modal-lead"
                >
                    <DialogHeader className="text-left">
                        <div className="flex items-start gap-3">
                            <img
                                src="/apple-touch-icon.png"
                                alt="CondominioNow"
                                width={48}
                                height={48}
                                className="size-12 rounded-xl shrink-0 border border-slate-200"
                            />
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-widest text-primary font-semibold">
                                    {install.mode === "samsung" ? "Consigliato" : "Installa l'app"}
                                </div>
                                <DialogTitle className="mt-0.5 font-serif text-lg font-semibold text-slate-900 leading-tight">
                                    {install.mode === "samsung" && "Installa CondominioNow con Google Chrome"}
                                    {install.mode === "chrome-native" && "Installa CondominioNow"}
                                    {install.mode === "chrome-manual" && "Installa dal menu di Chrome"}
                                    {install.mode === "ios" && "CondominioNow sulla tua Home"}
                                </DialogTitle>
                            </div>
                        </div>
                        <DialogDescription asChild>
                            <span id="install-modal-lead" className="block">
                                <InstallLead mode={install.mode} />
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    <InstallActions
                        mode={install.mode}
                        deferredPrompt={install.deferredPrompt}
                        installing={install.installing}
                        showSamsungFallback={install.showSamsungFallback}
                        setShowSamsungFallback={install.setShowSamsungFallback}
                        copied={install.copied}
                        onNativeInstall={handleNative}
                        onOpenChrome={install.openInChrome}
                        onCopyLink={install.copyLink}
                        onDismiss={null}
                        testIdPrefix="home-install-modal"
                    />
                </DialogContent>
            </Dialog>
        </section>
    );
}
